import {
  makeStyles,
  Theme,
  Typography,
  useTheme,
  useMediaQuery,
} from "@material-ui/core";
import clsx from "clsx";
import { Services } from "@configuratorware/configurator-frontendgui/App/ServiceLocator";
import { t } from "@configuratorware/configurator-frontendgui/Framework/i18n";
import React, { useEffect, useState } from "react";
import NavigationMobile from "./Navigation-Mobile";
import NavigationDesktop from "./Navigation-Desktop";
import { THeaderNavigtationItem } from "../../Types/Types";
import LoginContainer from "../../Containers/Login/Login";
import BasketList from "../../Containers/BasketList/BasketList";
import OrderList from "../../Containers/OrderList/OrderList";
import SkiSchoolSelector from "../../Containers/SkiSchoolSelector/SkiSchoolSelector";
import { useSelector } from "react-redux";

const useStyles = makeStyles(
  (theme: Theme) => ({
    root: {
      width: "100%",
      boxSizing: "border-box",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      padding: `${theme.spacing(2)}px ${theme.spacing(4)}px`,
    },

    logo: {
      // TODO: Apply custom font in the future
      fontSize: "1.25rem",
      fontWeight: 700,
      [theme.breakpoints.down("sm")]: {
        width: "70vw",
      },
    },
  }),
  { name: "CustomHeader" }
);

interface CustomHeaderProps {
  item: Store.Configurator.Configuration.TItem;
  isLoggedIn: boolean;
  setLogOff: () => void;
  fetchCategories: () => void;
  isValidLogin: boolean;
  selectedSkiSchool: any;
  hideActions: boolean;
  hideTitle: boolean;
  className: string;
  categories: any[];
  showOutfit: (isVisible: boolean, screenshot: any) => void;
  getScreenshotImage: Function;
  validateConfiguration: any;
}

export const PANTS_CATEGORIES = ["hosen_dermizax", "hosen_goretex", "pants"];
const JACKET_CATEGORIES = ["jacken_goretex", "jacken_goretex", "jacket"];

const CustomHeader = ({
  item,
  isLoggedIn,
  isValidLogin,
  selectedSkiSchool,
  setLogOff,
  fetchCategories,
  className,
  hideActions = false,
  hideTitle = false,
  categories,
  showOutfit,
  validateConfiguration,
  getScreenshotImage,
}: CustomHeaderProps) => {
  const classes = useStyles({});
  const [showLogin, setShowLogin] = useState(false);
  const [showSkiSchoolSelector, setShowSkiSchoolSelector] = useState(false);
  const [showBasketList, setShowBasketList] = useState(false);
  const [showOrderList, setShowOrderList] = useState(false);

  // @ts-ignore
  const basketItems = useSelector((state) => state.basket.items);

  const itemIdentifier = useSelector(
    // @ts-ignore
    (state) => state.configurator.configuration.item.identifier
  );

  useEffect(() => {
    !isValidLogin && showLogin && setShowLogin(false);
    isValidLogin && !selectedSkiSchool && setShowSkiSchoolSelector(true);
  }, [isValidLogin]);

  const theme = useTheme();
  const isMd = useMediaQuery(theme.breakpoints.down("sm"));

  const [showOutfitInMenu, setShowOutfitInMenu] = useState<boolean>(false);

  useEffect(() => {
    if (
      !categories ||
      (Object.keys(categories).length === 0 && !Array.isArray(categories))
    ) {
      fetchCategories();
    }
  }, []);

  useEffect(() => {
    shouldShowOutfit();
  }, [window.location, item, basketItems, categories]);

  const shouldShowOutfit = () => {
    let isVisible = true;

    const basketItemCategories = [];
    let activeItemCategory;
    if (categories && categories.length) {
      categories.forEach((category) => {
        if (
          PANTS_CATEGORIES.includes(category.category) ||
          JACKET_CATEGORIES.includes(category.category)
        ) {
          const cat = PANTS_CATEGORIES.includes(category.category)
            ? "pants"
            : "jacket";

          category.items.forEach((categoryItem) => {
            if (categoryItem.identifier === item.identifier) {
              activeItemCategory = cat;
            } else if (categoryItem.subCategories) {
              categoryItem.subCategories.forEach((subCat) => {
                if (subCat.identifier === item.identifier) {
                  activeItemCategory = cat;
                }
              });
            }

            basketItems.forEach((basketItem) => {
              if (
                categoryItem.identifier ===
                basketItem.configuration.item.identifier
              ) {
                basketItemCategories.push(cat);
              } else if (categoryItem.subCategories) {
                categoryItem.subCategories.forEach((subCat) => {
                  if (
                    subCat.identifier ===
                    basketItem.configuration.item.identifier
                  ) {
                    basketItemCategories.push(cat);
                  }
                });
              }
            });
          });
        }
      });
    }

    const allBasketItemHasSameCategory =
      new Set(basketItemCategories).size === 1;

    if (
      basketItems.length === 0 ||
      (basketItems.length === 1 &&
        item.identifier === basketItems[0].configuration.item.identifier) ||
      window.location.pathname === "/products/overview" ||
      (allBasketItemHasSameCategory &&
        basketItemCategories[0] === activeItemCategory)
    ) {
      isVisible = false;
    }

    setShowOutfitInMenu(isVisible);
  };

  const hasNoAccess = useSelector(
    // @ts-ignore
    (state) => state.configurator.hasNoAccess
  );

  const headerNavigationItems: THeaderNavigtationItem[] = [
    {
      label: isLoggedIn ? t("headerMenu.logoff") : t("headerMenu.login"),
      type: "option",
      icon: "person",
      isVisible: true,
      onClick: () => (isLoggedIn ? setLogOff() : setShowLogin(true)),
    },
    {
      label: isLoggedIn ? t("headerMenu.basket") : t("headerMenu.requestList"),
      type: "option",
      icon: "shopping_cart",
      isVisible: !hasNoAccess,
      onClick: () => setShowBasketList(true),
    },
    {
      label: t("headerMenu.outfit"),
      type: "option",
      icon: "filter",
      isVisible: showOutfitInMenu,
      onClick: async () => {
        const screenshot = await getScreenshotImage();

        try {
          await Services.configurator.validateConfiguration();
          showOutfit(true, screenshot);
        } catch (e) {
          // eslint-disable-next-line no-console
          console.warn(`Error while validating configuration: ${e}`);
        }
      },
    },
    {
      label: t("headerMenu.purchaseHistory"),
      type: "option",
      icon: "history",
      isVisible: isLoggedIn,
      onClick: () => setShowOrderList(true),
    },
    {
      label: t("headerMenu.save"),
      type: "action",
      icon: "cloud_download",
      isVisible: !hideActions,
      onClick: () => Services.interaction.saveConfiguration(),
    },
    {
      label: t("headerMenu.load"),
      type: "action",
      icon: "backup",
      isVisible: !hideActions,
      onClick: () => Services.interaction.loadConfiguration(),
    },
    {
      label: t("headerMenu.sendEmail"),
      type: "action",
      icon: "mail",
      isVisible: !hideActions,
      onClick: async () => {
        await Services.interaction.mailConfiguration();
        Services.interaction.showSendEmailDialog();
      },
    },
    {
      label: t("headerMenu.saveAsPdf"),
      type: "action",
      icon: "picture_as_pdf",
      isVisible: !hideActions,
      onClick: async () => Services.interaction.printConfigurationAndOpenPdf(),
    },
  ];

  return (
    <div className={clsx(classes.root, className)}>
      {!hideTitle && (
        <Typography variant="body1" className={classes.logo}>
          {item?.title}
        </Typography>
      )}
      {!isMd ? (
        <NavigationDesktop items={headerNavigationItems} />
      ) : (
        <NavigationMobile items={headerNavigationItems} />
      )}
      <LoginContainer
        show={showLogin && !isValidLogin}
        onClose={() => setShowLogin(false)}
      />
      <BasketList
        show={showBasketList}
        onClose={() => setShowBasketList(false)}
      />
      <OrderList show={showOrderList} onClose={() => setShowOrderList(false)} />
      <SkiSchoolSelector
        show={isValidLogin && showSkiSchoolSelector}
        onClose={() => setShowSkiSchoolSelector(false)}
      />
    </div>
  );
};

export default CustomHeader;
